import React, { useState, useMemo, useCallback, useEffect } from 'react'

import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Typography, Modal, Divider, Button, Form, message } from 'antd'

import { observer } from 'mobx-react-lite'

import { Box } from 'common/components/boxes'
import { Drawer, DrawerRef } from 'common/components/Drawer'
import { Loading } from 'common/components/Loading'
import { useQuery } from 'common/hooks/use-query'
import { VendorUserTiers } from 'common/server/server_types'

import { SelectUser } from 'vendor/components/SelectUser'
import { useVendorStores } from 'vendor/hooks/use-stores'
import { UpdateVendorCompany } from 'vendor/server/vendor_companies'

type UpdateCustomerProps = {
  vendorCompanyId: string
  onClose: () => void
}

export const UpdateCustomer = observer<UpdateCustomerProps, DrawerRef>(
  ({ vendorCompanyId, onClose }, ref) => {
    const { vendorCompanyStore, vendorSettingStore } = useVendorStores()

    const vendorCompany = vendorCompanyStore.selectedVendorCompany

    const [isSubmitting, setSubmitting] = useState(false)
    const [form] = Form.useForm()

    useQuery(vendorSettingStore.indexUsers)
    const { isLoading } = useQuery(() => {
      if (!vendorCompanyId) return Promise.resolve()
      return vendorCompanyStore.getVendorCompany(vendorCompanyId)
    }, [vendorCompanyId])

    useEffect(() => {
      if (vendorCompany) {
        form.setFieldsValue({
          defaultWatcherVendorUserIds: vendorCompany?.default_watcher_vendor_user_ids,
        })
      }
    }, [vendorCompany])

    const updateCustomer = useCallback(
      async (vendorCompanyParams: UpdateVendorCompany) => {
        setSubmitting(true)
        try {
          await vendorCompanyStore.updateVendorCompany(vendorCompanyParams)
          vendorCompanyStore.getVendorCompanies()
          message.success(
            <>
              Successfully updated customer.
              {vendorCompanyParams.apply_retroactively && (
                <>
                  <br />
                  The orders can take time to update. Please allow a few seconds for your changes to appear.
                </>
              )}
            </>,
          )
          onClose()
        } catch (error) {
          if (error?.response?.data?.error) {
            message.error(error.response.data.error)
          } else {
            message.error('Unable to save customer')
          }
        } finally {
          setSubmitting(false)
        }
      },
      [vendorCompany, onClose],
    )

    const handleFinish = useCallback(
      async (formValues) => {
        const params = {
          id: vendorCompany?.id,
          default_watcher_vendor_user_ids: formValues?.defaultWatcherVendorUserIds,
        }

        Modal.confirm({
          title: 'Do you want these changes to apply to previous orders or only move forward?',
          icon: <ExclamationCircleOutlined />,
          okText: 'Yes',
          cancelText: 'No',
          onOk() {
            updateCustomer({ ...params, apply_retroactively: true })
          },
          onCancel() {
            updateCustomer({ ...params, apply_retroactively: false })
          },
        })
      },
      [vendorCompany, onClose],
    )

    const content = useMemo(() => {
      if (isLoading) {
        return <Loading />
      }

      return (
        <Form
          key={vendorCompany?.id}
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          scrollToFirstError
          style={{ width: '100%', height: '100%' }}
          initialValues={{
            defaultWatcherVendorUserIds: vendorCompany?.default_watcher_vendor_user_ids,
          }}
        >
          <Box
            px={{ _: 12, xs: 20 }}
            py={16}
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            overflowY="auto"
          >
            <Box flexGrow={1}>
              <Form.Item name="defaultWatcherVendorUserIds" label="Default Watchers">
                <SelectUser
                  makeOption={(user) => ({
                    value: user.id,
                    label: user?.name || user?.email,
                    disabled: user?.tier !== VendorUserTiers.PREMIUM,
                  })}
                  placeholder="Search for user"
                  mode="multiple"
                  showAssignToMe={false}
                />
              </Form.Item>

              <Typography.Link
                onClick={() =>
                  Modal.info({
                    title: "What's a default watcher?",
                    content: (
                      <>
                        <p> A watcher is someone that is notified of any changes to an order.</p>
                        <p>
                          A default watcher is someone that is automatically added as a watcher whenever a particular
                          customer places an order.
                        </p>
                        <p> Only premium users can be default watchers of a customer.</p>
                      </>
                    ),
                  })
                }
              >
                What&apos;s a default watcher?
              </Typography.Link>
            </Box>

            <Box>
              <Divider />

              <Box display="flex" justifyContent="space-between">
                <Button onClick={onClose} loading={isSubmitting} style={{ width: 100 }}>
                  Cancel
                </Button>

                <Button type="primary" htmlType="submit" loading={isSubmitting} style={{ width: 100 }}>
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </Form>
      )
    }, [isLoading, vendorCompany, isSubmitting, onClose, handleFinish])

    return (
      <Drawer width={378} bgGray ref={ref} onClose={onClose} closable={false} title={vendorCompany?.company?.name}>
        {content}
      </Drawer>
    )
  },
  { forwardRef: true },
)
